import { isAxiosError } from "axios";

/**
 * Throw from server or client when an error should render the route `error.tsx`
 * with a specific HTTP status illustration (400 / 403 / 404 / 500).
 *
 * Note: `shouldRenderNotFound()` only treats **404 / 410** as missing resources
 * (`not-found.tsx`). For other HTTP errors, prefer `rethrowForErrorBoundary()`
 * after that check so `error.tsx` can show the right status art.
 */
export class HttpStatusError extends Error {
  readonly statusCode: number;

  constructor(statusCode: number, message?: string) {
    const safeMessage =
      message?.trim() ||
      (statusCode === 400
        ? "Bad Request"
        : statusCode === 403
          ? "Forbidden"
          : statusCode === 404
            ? "Not Found"
            : "Something went wrong");

    super(`HTTP_STATUS:${statusCode}:${safeMessage}`);
    this.name = "HttpStatusError";
    this.statusCode = statusCode;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export function getHttpStatusFromError(error: unknown): number | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const record = error as Record<string, unknown>;

  if (record.name === "HttpStatusError" && typeof record.statusCode === "number") {
    return normalizeHttpStatus(record.statusCode);
  }

  if (typeof record.statusCode === "number") {
    return normalizeHttpStatus(record.statusCode);
  }

  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (typeof status === "number") {
      return normalizeHttpStatus(status);
    }
  }

  if (error instanceof Error) {
    const fromMessage = error.message.match(/HTTP_STATUS:(\d{3})\b/);
    if (fromMessage) {
      return normalizeHttpStatus(parseInt(fromMessage[1], 10));
    }
  }

  return null;
}

function normalizeHttpStatus(status: number): number | null {
  if (!Number.isFinite(status) || status < 400 || status > 599) {
    return null;
  }
  return status;
}

/** Image + alt for global error UI (`error.tsx`) and client pages (e.g. payment). */
export function getHttpErrorIllustrationForStatus(
  status: number | null
): { src: string; alt: string } {
  if (status === 400) {
    return { src: "/images/400.png", alt: "400 Bad Request" };
  }
  if (status === 403) {
    return { src: "/images/403.svg", alt: "403 Forbidden" };
  }
  if (status === 404) {
    return { src: "/images/404.svg", alt: "404 Page not found" };
  }
  return { src: "/images/500.svg", alt: "500 Internal Server Error" };
}

export function getHttpErrorIllustrationForUnknownError(
  error: unknown
): { src: string; alt: string } {
  const status = getHttpStatusFromError(error);
  return getHttpErrorIllustrationForStatus(status);
}

/**
 * Use in `catch` after `shouldRenderNotFound` is false so Axios HTTP failures
 * become `HttpStatusError` and `error.tsx` can reliably show 400/403/500 art
 * even when the raw Axios error shape is lost across the RSC boundary.
 */
export function rethrowForErrorBoundary(error: unknown): never {
  const existingStatus = getHttpStatusFromError(error);
  if (existingStatus !== null) {
    if (error instanceof HttpStatusError) {
      throw error;
    }
    const message =
      error instanceof Error ? error.message : `HTTP ${existingStatus}`;
    throw new HttpStatusError(existingStatus, message);
  }

  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (typeof status === "number" && status >= 400 && status < 600) {
      throw new HttpStatusError(status, error.message);
    }
  }
  throw error;
}
